import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FooterLinkConfig {
  href: string;
  internal?: boolean;
}

interface LinkColConfig {
  titleKey: string;
  linksKey: string;
  links: FooterLinkConfig[];
}

const LINK_COLS: LinkColConfig[] = [
  {
    titleKey: 'landing.footer.columns.product.title',
    linksKey: 'landing.footer.columns.product.links',
    links: [
      { href: '#' },
      { href: '#precos' },
      { href: '#' },
      { href: '#' },
      { href: '#' },
    ],
  },
  {
    titleKey: 'landing.footer.columns.company.title',
    linksKey: 'landing.footer.columns.company.links',
    links: [
      { href: '#' },
      { href: '#' },
      { href: '#' },
      { href: '#' },
      { href: '#' },
    ],
  },
  {
    titleKey: 'landing.footer.columns.resources.title',
    linksKey: 'landing.footer.columns.resources.links',
    links: [
      { href: '#' },
      { href: '#' },
      { href: '#' },
      { href: '#' },
      { href: '#' },
    ],
  },
  {
    titleKey: 'landing.footer.columns.legal.title',
    linksKey: 'landing.footer.columns.legal.links',
    links: [
      { href: '#' },
      { href: '/privacidade', internal: true },
      { href: '#' },
      { href: '#' },
      { href: '#' },
    ],
  },
];

interface Social {
  name: string;
  icon: React.ReactNode;
}

const SOCIALS: Social[] = [
  {
    name: 'Twitter',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    name: 'LinkedIn',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: 'GitHub',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
      </svg>
    ),
  },
  {
    name: 'YouTube',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
];

export const LandingFooter = React.memo(() => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white border-t border-gray-200 pt-16 pb-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10 mb-12">
          <div className="col-span-2 max-w-xs">
            <div className="text-xl font-semibold text-black tracking-tight mb-3">OS Works</div>
            <p className="text-sm leading-relaxed mb-5 text-gray-500">{t('landing.footer.tagline')}</p>
            <div className="flex items-center gap-2">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href="#"
                  aria-label={s.name}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-black hover:text-white hover:border-black transition-colors"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {LINK_COLS.map((col) => {
            const labels = t(col.linksKey, { returnObjects: true }) as string[];
            return (
              <div key={col.titleKey}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-4 text-black">{t(col.titleKey)}</div>
                <ul className="space-y-3">
                  {col.links.map((l, i) =>
                    l.internal ? (
                      <li key={i}>
                        <Link to={l.href} className="text-sm transition-colors hover:text-black text-gray-500">
                          {labels[i]}
                        </Link>
                      </li>
                    ) : (
                      <li key={i}>
                        <a href={l.href} className="text-sm transition-colors hover:text-black text-gray-500">
                          {labels[i]}
                        </a>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="border-t border-gray-100 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>{t('landing.footer.copyright', { year: currentYear })}</span>
            <span className="hidden md:inline">·</span>
            <span>{t('landing.footer.madeWith')}</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
              {t('landing.footer.systemsOperational')}
            </span>
            <a href="#" className="hover:text-black transition-colors text-gray-500">
              {t('landing.footer.statusLink')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
});
LandingFooter.displayName = 'LandingFooter';
